import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { lt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { ExhibitionDeletePasswordDialog } from './ExhibitionDeletePasswordDialog';

const timeStringToDate = (timeStr: string): Date | null => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return new Date(2000, 0, 1, h, m, 0);
};

const dateToTimeString = (d: Date | null): string => {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const DEFAULT_LANGUAGE = 'LT';

interface Excursion {
  id: string;
  title: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location?: string;
  maxParticipants?: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  isActive: boolean;
  notes?: string;
  language?: string | null;
  clientContacts?: string | null;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedColleague?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  initiator?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Colleague {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  department?: string;
}

interface ExcursionFormData {
  title: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  notes: string;
  language: string;
  clientContacts: string;
  assignedColleagueId: string;
  initiatorUserId: string;
}

interface ExcursionTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  notes: string;
  language: string;
  clientContacts: string;
  assignedColleagueId?: string;
  initiatorUserId?: string;
}

const emptyExcursionForm = (): ExcursionFormData => ({
  title: '',
  description: '',
  eventDate: '',
  startTime: '',
  endTime: '',
  location: '',
  maxParticipants: 0,
  notes: '',
  language: DEFAULT_LANGUAGE,
  clientContacts: '',
  assignedColleagueId: '',
  initiatorUserId: ''
});

const emptyTemplateForm = (): ExcursionTemplate => ({
  id: '',
  name: '',
  title: '',
  description: '',
  startTime: '09:00',
  endTime: '10:00',
  location: '',
  maxParticipants: 0,
  notes: '',
  language: DEFAULT_LANGUAGE,
  clientContacts: '',
  assignedColleagueId: '',
  initiatorUserId: ''
});

const colleagueLabel = (c: Colleague) =>
  `${c.firstName} ${c.lastName}${c.department ? ` (${c.department})` : ''}`;

const EXCURSION_TEMPLATES_KEY = 'excursion_templates_v1';

const Excursions: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [excursions, setExcursions] = useState<Excursion[]>([]);
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExcursion, setEditingExcursion] = useState<Excursion | null>(null);
  const [formData, setFormData] = useState<ExcursionFormData>(emptyExcursionForm);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ExcursionTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState<ExcursionTemplate>(emptyTemplateForm);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const canManageExcursions = hasPermission('excursions.manage');
  const formReadOnly = !canManageExcursions;
  const minEventDateStr = format(new Date(), 'yyyy-MM-dd');

  // Separate upcoming, completed and cancelled excursions
  const upcomingExcursions = excursions.filter(excursion => 
    excursion.status === 'scheduled' && excursion.isActive
  );
  
  const completedExcursions = excursions.filter(excursion => 
    excursion.status === 'completed' && excursion.isActive
  );

  const cancelledExcursions = excursions.filter(excursion =>
    excursion.status === 'cancelled' && excursion.isActive
  );

  const fetchExcursions = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/excursions'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch excursions');
      }

      const data = await response.json();
      setExcursions(data.excursions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch excursions');
    } finally {
      setLoading(false);
    }
  };

  const fetchColleagues = async () => {
    if (!canManageExcursions) return;
    
    try {
      const response = await fetch(getApiUrl('/excursions/colleagues/available'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch colleagues:', response.status, errorData);
        throw new Error(errorData.error || 'Failed to fetch colleagues');
      }

      const data = await response.json();
      console.log('Fetched colleagues:', data.colleagues?.length || 0, 'colleagues');
      setColleagues(data.colleagues || []);
    } catch (err) {
      console.error('Failed to fetch colleagues:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch colleagues');
    }
  };

  useEffect(() => {
    fetchExcursions();
    fetchColleagues();
  }, [canManageExcursions]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(EXCURSION_TEMPLATES_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as ExcursionTemplate[];
      if (Array.isArray(parsed)) {
        setTemplates(
          parsed.map((tpl) => ({
            ...tpl,
            language: tpl.language || DEFAULT_LANGUAGE,
            clientContacts: tpl.clientContacts || ''
          }))
        );
      }
    } catch (e) {
      console.error('Failed to load excursion templates', e);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(EXCURSION_TEMPLATES_KEY, JSON.stringify(templates));
  }, [templates]);

  const resetTemplateForm = () => {
    setEditingTemplateId(null);
    setTemplateForm(emptyTemplateForm());
  };

  const saveTemplate = () => {
    if (!templateForm.name.trim() || !templateForm.title.trim()) {
      return;
    }

    if (editingTemplateId) {
      setTemplates(prev =>
        prev.map(t => (t.id === editingTemplateId ? { ...templateForm, id: editingTemplateId } : t))
      );
    } else {
      const id = crypto.randomUUID();
      setTemplates(prev => [...prev, { ...templateForm, id }]);
    }

    resetTemplateForm();
  };

  const editTemplate = (template: ExcursionTemplate) => {
    setEditingTemplateId(template.id);
    setTemplateForm({
      ...template,
      language: template.language || DEFAULT_LANGUAGE,
      clientContacts: template.clientContacts || '',
      assignedColleagueId: template.assignedColleagueId || '',
      initiatorUserId: template.initiatorUserId || ''
    });
  };

  const deleteTemplate = (templateId: string) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId('');
    }
    if (editingTemplateId === templateId) {
      resetTemplateForm();
    }
  };

  const applyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setFormData(prev => ({
      ...prev,
      title: template.title,
      description: template.description,
      startTime: template.startTime,
      endTime: template.endTime,
      location: template.location,
      maxParticipants: template.maxParticipants,
      notes: template.notes,
      language: template.language || DEFAULT_LANGUAGE,
      clientContacts: template.clientContacts || '',
      assignedColleagueId: template.assignedColleagueId || '',
      initiatorUserId: template.initiatorUserId || ''
    }));
  };

  const handleOpenDialog = (excursion?: Excursion) => {
    setDialogError(null); // Clear previous errors
    setSelectedTemplateId('');
    if (excursion) {
      setEditingExcursion(excursion);
      setFormData({
        title: excursion.title,
        description: excursion.description,
        eventDate: excursion.eventDate.split('T')[0],
        startTime: excursion.startTime,
        endTime: excursion.endTime,
        location: excursion.location || '',
        maxParticipants: excursion.maxParticipants || 0,
        notes: excursion.notes || '',
        language: excursion.language || DEFAULT_LANGUAGE,
        clientContacts: excursion.clientContacts || '',
        assignedColleagueId: excursion.assignedColleague?.id || '',
        initiatorUserId: excursion.initiator?.id || ''
      });
    } else {
      setEditingExcursion(null);
      setFormData(emptyExcursionForm());
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExcursion(null);
    setDialogError(null); // Clear errors
    setFormData(emptyExcursionForm());
  };

  const openDialogLoadError = (message: string) => {
    setEditingExcursion(null);
    setSelectedTemplateId('');
    setFormData(emptyExcursionForm());
    setDialogError(message);
    setDialogOpen(true);
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        id?: string;
        date?: string;
        excursion?: Excursion | null;
      }>;
      const detail = customEvent.detail || {};

      if (detail.excursion) {
        handleOpenDialog(detail.excursion);
        return;
      }

      if (detail.id) {
        const existing = excursions.find((e) => e.id === detail.id);
        if (existing) {
          handleOpenDialog(existing);
          return;
        }
        void (async () => {
          try {
            const res = await fetch(getApiUrl(`/excursions/${detail.id}`), {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
              openDialogLoadError(
                (payload as { error?: string }).error || t('excursions.loadFailed', 'Could not load excursion')
              );
              return;
            }
            const ex = (payload as { excursion?: Excursion }).excursion;
            if (ex) {
              handleOpenDialog(ex);
            }
          } catch (e) {
            openDialogLoadError(
              e instanceof Error ? e.message : t('excursions.loadFailed', 'Could not load excursion')
            );
          }
        })();
        return;
      }

      if (detail.date) {
        if (!canManageExcursions) {
          return;
        }
        handleOpenDialog();
        setFormData((prev) => ({ ...prev, eventDate: detail.date || '' }));
      }
    };

    window.addEventListener('exhibition-calendar-open-excursion', handler as EventListener);
    return () => window.removeEventListener('exhibition-calendar-open-excursion', handler as EventListener);
  }, [excursions, canManageExcursions, t]);

  const handleFormChange = (field: keyof ExcursionFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    if (!canManageExcursions) {
      return;
    }
    try {
      const endpoint = editingExcursion 
        ? `/excursions/${editingExcursion.id}`
        : '/excursions';
      
      const method = editingExcursion ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        maxParticipants: formData.maxParticipants
      };

      const response = await fetch(getApiUrl(endpoint), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save excursion');
      }

      handleCloseDialog();
      fetchExcursions();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save excursion');
    }
  };

  const requestDeleteExcursion = (excursionId: string) => {
    setDeleteTargetId(excursionId);
  };

  const performDeleteExcursion = async (excursionId: string) => {
    try {
      const response = await fetch(getApiUrl(`/excursions/${excursionId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete excursion');
      }

      setDeleteTargetId(null);
      fetchExcursions();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete excursion');
      throw err;
    }
  };

  const handleStatusChange = async (excursionId: string, newStatus: string) => {
    try {
      const response = await fetch(getApiUrl(`/excursions/${excursionId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error(t('excursions.failedToUpdateStatus', 'Failed to update status'));
      }
      
      await fetchExcursions();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('excursions.failedToUpdateStatus', 'Failed to update status'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled': return t('excursions.status.scheduled', 'Scheduled');
      case 'completed': return t('excursions.status.completed', 'Completed');
      case 'cancelled': return t('excursions.status.cancelled', 'Cancelled');
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {!embedded ? (
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
            <EventIcon />
            {t('excursions.title', 'Ekskursijos')}
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1 }} />
        )}
        
        {/* Add Excursion FAB Button */}
        {canManageExcursions && (
          <Tooltip title="Add new excursion">
            <Fab
              color="primary"
              size="small"
              aria-label="add excursion"
              onClick={() => handleOpenDialog()}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for Excursions */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="excursion tabs"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 500,
              textTransform: 'none'
            },
            '& .Mui-selected': {
              color: 'primary.main',
              fontWeight: 600
            }
          }}
        >
          <Tab 
            label={`${t('excursions.upcoming', 'Upcoming Excursions')} (${upcomingExcursions.length})`}
            id="excursion-tab-0"
            aria-controls="excursion-tabpanel-0"
          />
          <Tab 
            label={`${t('excursions.completed', 'Completed Excursions')} (${completedExcursions.length})`}
            id="excursion-tab-1"
            aria-controls="excursion-tabpanel-1"
          />
          {canManageExcursions && (
            <Tab
              label={`${t('excursions.cancelled', 'Atšauktos ekskursijos')} (${cancelledExcursions.length})`}
              id="excursion-tab-2"
              aria-controls="excursion-tabpanel-2"
            />
          )}
        </Tabs>
      </Box>

      {/* Tab Panel 0: Upcoming Excursions */}
      <div
        role="tabpanel"
        hidden={activeTab !== 0}
        id="excursion-tabpanel-0"
        aria-labelledby="excursion-tab-0"
        style={{
          transition: 'opacity 0.3s ease-in-out',
          opacity: activeTab === 0 ? 1 : 0
        }}
      >
        {activeTab === 0 && (
          <>
            {upcomingExcursions.length > 0 ? (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
                gap: 2 
              }}>
                {upcomingExcursions.map((excursion) => {
                  const isUnassigned = !excursion.assignedColleague;
                  return (
                  <Box key={excursion.id}>
                    <Card
                      sx={
                        isUnassigned
                          ? {
                              border: '2px solid',
                              borderColor: 'warning.main',
                              bgcolor: 'rgba(255, 167, 38, 0.08)'
                            }
                          : undefined
                      }
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                          <Typography variant="h6" component="h3">
                            {excursion.title}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                            <Chip 
                              label={getStatusLabel(excursion.status)} 
                              color={getStatusColor(excursion.status)}
                              size="small"
                            />
                            {isUnassigned && (
                              <Chip
                                label={t('excursions.unassigned', 'Nepriskirta')}
                                color="warning"
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                            )}
                          </Box>
                        </Box>
                        
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                        >
                          {excursion.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            📅 {formatDate(excursion.eventDate)} {formatTime(excursion.startTime)} - {formatTime(excursion.endTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            📍 {excursion.location}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            👥 {t('excursions.maxParticipantsLabel', 'Max participants')}: {excursion.maxParticipants}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            🌐 {t('excursions.language', 'Language')}: {(excursion.language || DEFAULT_LANGUAGE).toUpperCase()}
                          </Typography>
                          {excursion.clientContacts && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                              📞 {t('excursions.clientContacts', 'Contacts')}: {excursion.clientContacts}
                            </Typography>
                          )}
                          {excursion.assignedColleague ? (
                            <Typography variant="caption" color="text.secondary">
                              👨‍💼 {t('excursions.assignedLabel', 'Assigned')}: {excursion.assignedColleague.firstName} {excursion.assignedColleague.lastName}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700 }}>
                              {t('excursions.unassignedHint', 'Nepriskirta darbuotojui')}
                            </Typography>
                          )}
                          {excursion.initiator && (
                            <Typography variant="caption" color="text.secondary">
                              {t('excursions.initiatorLabel', 'Iniciatorius')}: {excursion.initiator.firstName}{' '}
                              {excursion.initiator.lastName}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                      
                      {canManageExcursions && (
                        <>
                          <Divider />
                          <CardActions sx={{ justifyContent: 'space-between' }}>
                            <Box>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>{t('excursions.statusLabel', 'Status')}</InputLabel>
                                <Select
                                  value={excursion.status}
                                  label={t('excursions.statusLabel', 'Status')}
                                  onChange={(e) => handleStatusChange(excursion.id, e.target.value)}
                                >
                                  <MenuItem value="scheduled">{t('excursions.status.scheduled', 'Scheduled')}</MenuItem>
                                  <MenuItem value="completed">{t('excursions.status.completed', 'Completed')}</MenuItem>
                                  <MenuItem value="cancelled">{t('excursions.status.cancelled', 'Cancelled')}</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(excursion)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => requestDeleteExcursion(excursion.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </Box>
                  );
                })}
              </Box>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="text.secondary" align="center">
                    {t('excursions.noUpcoming', 'No upcoming excursions scheduled')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Tab Panel 1: Completed Excursions */}
      <div
        role="tabpanel"
        hidden={activeTab !== 1}
        id="excursion-tabpanel-1"
        aria-labelledby="excursion-tab-1"
        style={{
          transition: 'opacity 0.3s ease-in-out',
          opacity: activeTab === 1 ? 1 : 0
        }}
      >
        {activeTab === 1 && (
          <>
            {completedExcursions.length > 0 ? (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: 2 
              }}>
                {completedExcursions.map((excursion) => (
                  <Box key={excursion.id}>
                    <Card sx={{ 
                      border: '2px solid', 
                      borderColor: 'success.main',
                      backgroundColor: 'success.light',
                      '& .MuiCardContent-root': {
                        backgroundColor: 'success.light'
                      }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="h3">
                            {excursion.title}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(excursion.status)} 
                            color={getStatusColor(excursion.status)}
                            size="small"
                          />
                        </Box>
                        
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                        >
                          {excursion.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            📅 {formatDate(excursion.eventDate)} {formatTime(excursion.startTime)} - {formatTime(excursion.endTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            📍 {excursion.location}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            👥 {t('excursions.maxParticipantsLabel', 'Max participants')}: {excursion.maxParticipants}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            🌐 {t('excursions.language', 'Language')}: {(excursion.language || DEFAULT_LANGUAGE).toUpperCase()}
                          </Typography>
                          {excursion.clientContacts && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                              📞 {t('excursions.clientContacts', 'Contacts')}: {excursion.clientContacts}
                            </Typography>
                          )}
                          {excursion.assignedColleague && (
                            <Typography variant="caption" color="text.secondary">
                              👨‍💼 {t('excursions.assignedLabel', 'Assigned')}: {excursion.assignedColleague.firstName} {excursion.assignedColleague.lastName}
                            </Typography>
                          )}
                          {excursion.initiator && (
                            <Typography variant="caption" color="text.secondary">
                              {t('excursions.initiatorLabel', 'Iniciatorius')}: {excursion.initiator.firstName}{' '}
                              {excursion.initiator.lastName}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                      
                      {canManageExcursions && (
                        <>
                          <Divider />
                          <CardActions sx={{ justifyContent: 'space-between' }}>
                            <Box>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>{t('excursions.statusLabel', 'Status')}</InputLabel>
                                <Select
                                  value={excursion.status}
                                  label={t('excursions.statusLabel', 'Status')}
                                  onChange={(e) => handleStatusChange(excursion.id, e.target.value)}
                                >
                                  <MenuItem value="scheduled">{t('excursions.status.scheduled', 'Scheduled')}</MenuItem>
                                  <MenuItem value="completed">{t('excursions.status.completed', 'Completed')}</MenuItem>
                                  <MenuItem value="cancelled">{t('excursions.status.cancelled', 'Cancelled')}</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(excursion)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => requestDeleteExcursion(excursion.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </Box>
                ))}
              </Box>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="text.secondary" align="center">
                    {t('excursions.noCompleted', 'No completed excursions yet')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Tab Panel 2: Cancelled Excursions (managers only) */}
      {canManageExcursions && (
        <div
          role="tabpanel"
          hidden={activeTab !== 2}
          id="excursion-tabpanel-2"
          aria-labelledby="excursion-tab-2"
          style={{
            transition: 'opacity 0.3s ease-in-out',
            opacity: activeTab === 2 ? 1 : 0
          }}
        >
          {activeTab === 2 && (
            <>
              {cancelledExcursions.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 2
                  }}
                >
                  {cancelledExcursions.map((excursion) => (
                    <Box key={excursion.id}>
                      <Card
                        sx={{
                          border: '2px solid',
                          borderColor: 'warning.main',
                          backgroundColor: 'warning.light',
                          '& .MuiCardContent-root': {
                            backgroundColor: 'warning.light'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="h6" component="h3">
                              {excursion.title}
                            </Typography>
                            <Chip
                              label={getStatusLabel(excursion.status)}
                              color={getStatusColor(excursion.status)}
                              size="small"
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                          >
                            {excursion.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              📅 {formatDate(excursion.eventDate)} {formatTime(excursion.startTime)} - {formatTime(excursion.endTime)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              📍 {excursion.location}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              👥 {t('excursions.maxParticipantsLabel', 'Max participants')}: {excursion.maxParticipants}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              🌐 {t('excursions.language', 'Language')}: {(excursion.language || DEFAULT_LANGUAGE).toUpperCase()}
                            </Typography>
                            {excursion.clientContacts && (
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                📞 {t('excursions.clientContacts', 'Contacts')}: {excursion.clientContacts}
                              </Typography>
                            )}
                            {excursion.assignedColleague && (
                              <Typography variant="caption" color="text.secondary">
                                👨‍💼 {t('excursions.assignedLabel', 'Assigned')}: {excursion.assignedColleague.firstName}{' '}
                                {excursion.assignedColleague.lastName}
                              </Typography>
                            )}
                            {excursion.initiator && (
                              <Typography variant="caption" color="text.secondary">
                                {t('excursions.initiatorLabel', 'Iniciatorius')}: {excursion.initiator.firstName}{' '}
                                {excursion.initiator.lastName}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>

                        {canManageExcursions && (
                          <>
                            <Divider />
                            <CardActions sx={{ justifyContent: 'space-between' }}>
                              <Box>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>{t('excursions.statusLabel', 'Status')}</InputLabel>
                                  <Select
                                    value={excursion.status}
                                    label={t('excursions.statusLabel', 'Status')}
                                    onChange={(e) => handleStatusChange(excursion.id, e.target.value)}
                                  >
                                    <MenuItem value="scheduled">{t('excursions.status.scheduled', 'Scheduled')}</MenuItem>
                                    <MenuItem value="completed">{t('excursions.status.completed', 'Completed')}</MenuItem>
                                    <MenuItem value="cancelled">{t('excursions.status.cancelled', 'Cancelled')}</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>

                              <Box>
                                <IconButton size="small" onClick={() => handleOpenDialog(excursion)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => requestDeleteExcursion(excursion.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </CardActions>
                          </>
                        )}
                      </Card>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Card>
                  <CardContent>
                    <Typography variant="body1" color="text.secondary" align="center">
                      {t('excursions.noCancelled', 'No cancelled excursions')}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {formReadOnly
            ? t('excursions.viewTitle', 'Excursion')
            : editingExcursion
              ? t('excursions.edit', 'Edit Excursion')
              : t('excursions.addNew', 'Add New Excursion')}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {canManageExcursions && !editingExcursion && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('excursions.template', 'Template')}</InputLabel>
                  <Select
                    value={selectedTemplateId}
                    label={t('excursions.template', 'Template')}
                    onChange={(e) => applyTemplate(e.target.value)}
                  >
                    <MenuItem value="">{t('excursions.noTemplate', 'No template')}</MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => setTemplatesDialogOpen(true)}>
                  {t('excursions.manageTemplates', 'Manage templates')}
                </Button>
              </Box>
            )}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: 2 
            }}>
              <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                <Autocomplete
                  options={colleagues}
                  disabled={formReadOnly}
                  value={colleagues.find((c) => c.id === formData.initiatorUserId) || null}
                  onChange={(_, value) =>
                    setFormData({ ...formData, initiatorUserId: value?.id || '' })
                  }
                  getOptionLabel={(option) => colleagueLabel(option)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('excursions.initiator', 'Iniciatorius')}
                      placeholder={t('excursions.initiatorPlaceholder', 'Pradėkite rašyti vardą…')}
                    />
                  )}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label={t('excursions.titleField', 'Ekskursijos pavadinimas')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  disabled={formReadOnly}
                />
              </Box>
              
              <Box>
                <TextField
                  fullWidth
                  label={t('excursions.description', 'Description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('excursions.descriptionPlaceholder', 'Write a description (multiple lines supported)…')}
                  multiline
                  minRows={3}
                  maxRows={10}
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      lineHeight: 1.5
                    }
                  }}
                  helperText={t('excursions.multilineHint', 'Tip: use Enter for new lines')}
                  required
                  disabled={formReadOnly}
                />
              </Box>
              
              <Box>
                <TextField
                  fullWidth
                  label={t('excursions.eventDate', 'Event Date')}
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={formReadOnly}
                  inputProps={{
                    min: !editingExcursion ? minEventDateStr : undefined
                  }}
                />
              </Box>
              
              <Box>
                <TimePicker
                  label={t('excursions.startTime', 'Start Time')}
                  value={timeStringToDate(formData.startTime)}
                  onChange={(time) => setFormData({ ...formData, startTime: dateToTimeString(time) })}
                  timeSteps={{ minutes: 15 }}
                  minTime={new Date(2000, 0, 1, 9, 0, 0)}
                  maxTime={new Date(2000, 0, 1, 21, 0, 0)}
                  skipDisabled
                  disabled={formReadOnly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Box>
              
              <Box>
                <TimePicker
                  label={t('excursions.endTime', 'End Time')}
                  value={timeStringToDate(formData.endTime)}
                  onChange={(time) => setFormData({ ...formData, endTime: dateToTimeString(time) })}
                  timeSteps={{ minutes: 15 }}
                  minTime={new Date(2000, 0, 1, 9, 0, 0)}
                  maxTime={new Date(2000, 0, 1, 21, 0, 0)}
                  skipDisabled
                  disabled={formReadOnly}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Box>
              
              <Box>
                <TextField
                  fullWidth
                  label={t('excursions.location', 'Location')}
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  disabled={formReadOnly}
                />
              </Box>
              
              <Box>
                <TextField
                  fullWidth
                  label={t('excursions.maxParticipants', 'Max Participants')}
                  type="number"
                  value={formData.maxParticipants}
                  onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })}
                  inputProps={{ min: 1 }}
                  required
                  disabled={formReadOnly}
                />
              </Box>
              
              <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                <FormControl fullWidth disabled={formReadOnly}>
                  <InputLabel>{t('excursions.assignColleague', 'Assign Colleague')}</InputLabel>
                  <Select
                    value={formData.assignedColleagueId || ''}
                    onChange={(e) => setFormData({ ...formData, assignedColleagueId: e.target.value })}
                    label={t('excursions.assignColleague', 'Assign Colleague')}
                  >
                    <MenuItem value="">
                      <em>{t('excursions.noAssignment', 'No assignment')}</em>
                    </MenuItem>
                    {colleagues.map((colleague) => (
                      <MenuItem key={colleague.id} value={colleague.id}>
                        {colleague.firstName} {colleague.lastName} 
                        {colleague.department && ` (${colleague.department})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                <TextField
                  fullWidth
                  label={t('excursions.notes', 'Notes')}
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('excursions.notesPlaceholder', 'Optional notes…')}
                  multiline
                  minRows={2}
                  maxRows={8}
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      lineHeight: 1.5
                    }
                  }}
                  disabled={formReadOnly}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  label={t('excursions.language', 'Language')}
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  helperText={t('excursions.languageDefaultHint', 'Default: LT')}
                  disabled={formReadOnly}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                <TextField
                  fullWidth
                  label={t('excursions.clientContacts', 'Contacts')}
                  value={formData.clientContacts}
                  onChange={(e) => setFormData({ ...formData, clientContacts: e.target.value })}
                  placeholder={t('excursions.clientContactsPlaceholder', 'Client contacts…')}
                  multiline
                  minRows={2}
                  maxRows={6}
                  disabled={formReadOnly}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {formReadOnly ? t('common.close', 'Close') : t('excursions.cancel', 'Cancel')}
          </Button>
          {canManageExcursions && (
            <Button onClick={handleSubmit} variant="contained">
              {editingExcursion ? t('excursions.update', 'Update') : t('excursions.create', 'Create')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </LocalizationProvider>
      <Dialog
        open={templatesDialogOpen}
        onClose={() => {
          setTemplatesDialogOpen(false);
          resetTemplateForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('excursions.manageTemplates', 'Manage templates')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <TextField
              label={t('excursions.templateName', 'Template name')}
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('excursions.titleField', 'Ekskursijos pavadinimas')}
              value={templateForm.title}
              onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('excursions.description', 'Description')}
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label={t('excursions.location', 'Location')}
              value={templateForm.location}
              onChange={(e) => setTemplateForm({ ...templateForm, location: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('excursions.startTime', 'Start Time')}
              value={templateForm.startTime}
              onChange={(e) => setTemplateForm({ ...templateForm, startTime: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('excursions.endTime', 'End Time')}
              value={templateForm.endTime}
              onChange={(e) => setTemplateForm({ ...templateForm, endTime: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('excursions.maxParticipants', 'Max Participants')}
              type="number"
              value={templateForm.maxParticipants}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, maxParticipants: parseInt(e.target.value) || 0 })
              }
              fullWidth
            />
            <TextField
              label={t('excursions.notes', 'Notes')}
              value={templateForm.notes}
              onChange={(e) => setTemplateForm({ ...templateForm, notes: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label={t('excursions.language', 'Language')}
              value={templateForm.language}
              onChange={(e) => setTemplateForm({ ...templateForm, language: e.target.value })}
              helperText={t('excursions.languageDefaultHint', 'Default: LT')}
              fullWidth
            />
            <TextField
              label={t('excursions.clientContacts', 'Contacts')}
              value={templateForm.clientContacts}
              onChange={(e) => setTemplateForm({ ...templateForm, clientContacts: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('excursions.assignColleague', 'Priskirti kolegą')}</InputLabel>
              <Select
                value={templateForm.assignedColleagueId || ''}
                label={t('excursions.assignColleague', 'Priskirti kolegą')}
                onChange={(e) => setTemplateForm({ ...templateForm, assignedColleagueId: e.target.value })}
              >
                <MenuItem value="">
                  <em>{t('excursions.noAssignment', 'No assignment')}</em>
                </MenuItem>
                {colleagues.map((colleague) => (
                  <MenuItem key={colleague.id} value={colleague.id}>
                    {colleague.firstName} {colleague.lastName}
                    {colleague.department && ` (${colleague.department})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              options={colleagues}
              value={colleagues.find((c) => c.id === templateForm.initiatorUserId) || null}
              onChange={(_, value) =>
                setTemplateForm({ ...templateForm, initiatorUserId: value?.id || '' })
              }
              getOptionLabel={(option) => colleagueLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('excursions.initiator', 'Iniciatorius')}
                  placeholder={t('excursions.initiatorPlaceholder', 'Pradėkite rašyti vardą…')}
                />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={saveTemplate}>
              {editingTemplateId
                ? t('excursions.updateTemplate', 'Update template')
                : t('excursions.saveTemplate', 'Save template')}
            </Button>
            {editingTemplateId && (
              <Button onClick={resetTemplateForm}>{t('common.cancel', 'Cancel')}</Button>
            )}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {templates.map((template) => (
              <Card key={template.id} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2">{template.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {template.title}
                      </Typography>
                      {template.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '2.4em' }}>
                          {template.description.length > 80 ? `${template.description.slice(0, 80)}…` : template.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => editTemplate(template)}>
                        {t('common.edit', 'Edit')}
                      </Button>
                      <Button size="small" color="error" onClick={() => deleteTemplate(template.id)}>
                        {t('common.delete', 'Delete')}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('excursions.noTemplates', 'No templates yet')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTemplatesDialogOpen(false);
              resetTemplateForm();
            }}
          >
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      <ExhibitionDeletePasswordDialog
        open={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onVerified={async () => {
          if (deleteTargetId) {
            await performDeleteExcursion(deleteTargetId);
          }
        }}
      />
    </Box>
  );
};

export default Excursions;
